import React, { useState } from 'react';
import { Package, Truck, Plus, Trash2, Calculator, ArrowRight, ChevronRight, DollarSign, X as CloseIcon, FileText, Calendar, Tag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScreenInit } from '../../useScreenInit.js';
export function InventoryView() {
  const screenInit = useScreenInit();
  const [activeTab, setActiveTab] = useState<'open' | 'receive' | 'close'>(screenInit?.activeTab || 'receive');
  const [showInvoiceModal, setShowInvoiceModal] = useState(screenInit?.showInvoiceModal || false);
  const [newInvoice, setNewInvoice] = useState({
    vendor: '',
    category: 'Food',
    subCategory: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    remark: ''
  });
  const [invoices, setInvoices] = useState([{
    id: 1,
    vendor: 'Sysco Foods',
    category: 'Food',
    subCategory: 'Dairy',
    amount: 450.25,
    date: '2026-05-30'
  }, {
    id: 2,
    vendor: 'Restaurant Depot',
    category: 'Food',
    subCategory: 'Dry Goods',
    amount: 1200.0,
    date: '2026-05-29'
  }]);
  const [openInv, setOpenInv] = useState(12500);
  const [closeInv, setCloseInv] = useState(11800);
  const totalPurchases = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const cogs = openInv + totalPurchases - closeInv;
  const handleAddInvoice = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newInvoice.vendor || !newInvoice.amount) return;
    const invoiceToAdd = {
      id: invoices.length + 1,
      vendor: newInvoice.vendor,
      category: newInvoice.category,
      subCategory: newInvoice.subCategory,
      amount: parseFloat(newInvoice.amount),
      date: newInvoice.date
    };
    setInvoices([invoiceToAdd, ...invoices]);
    setNewInvoice({
      vendor: '',
      category: 'Food',
      subCategory: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      remark: ''
    });
    setShowInvoiceModal(false);
  };
  return <div className="space-y-6" data-id="element-418">
      {/* Add Invoice Modal */}
      <AnimatePresence data-id="element-419">
        {showInvoiceModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-420">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowInvoiceModal(false)} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" data-id="element-421" />
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} className="relative w-full max-w-lg bg-white rounded-[32px] shadow-2xl overflow-hidden" data-id="element-422">
              <div className="p-8" data-id="element-423">
                <div className="flex justify-between items-start mb-6" data-id="element-424">
                  <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600" data-id="element-425">
                    <Plus size={24} data-id="element-426" />
                  </div>
                  <button onClick={() => setShowInvoiceModal(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400" data-id="element-427">
                    <CloseIcon size={20} data-id="element-428" />
                  </button>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-429">
                  Add New Invoice
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-430">
                  Enter invoice details to update your inventory and COGS.
                </p>

                <form onSubmit={handleAddInvoice} className="space-y-5" data-id="element-431">
                  <div className="grid grid-cols-2 gap-4" data-id="element-432">
                    <div className="col-span-2" data-id="element-433">
                      <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider" data-id="element-434">
                        Vendor Name
                      </label>
                      <div className="relative" data-id="element-435">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-436">
                          <Truck size={18} className="text-slate-400" data-id="element-437" />
                        </div>
                        <input type="text" required value={newInvoice.vendor} onChange={e => setNewInvoice({
                      ...newInvoice,
                      vendor: e.target.value
                    })} placeholder="e.g. Sysco Foods" className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all" data-id="element-438" />
                      </div>
                    </div>

                    <div data-id="element-439">
                      <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider" data-id="element-440">
                        Category
                      </label>
                      <select value={newInvoice.category} onChange={e => setNewInvoice({
                    ...newInvoice,
                    category: e.target.value
                  })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none" data-id="element-441">
                        <option data-id="element-442">Food</option>
                        <option data-id="element-443">Beverage</option>
                        <option data-id="element-444">Supplies</option>
                        <option data-id="element-445">Other</option>
                      </select>
                    </div>

                    <div data-id="element-446">
                      <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider" data-id="element-447">
                        Sub-Category
                      </label>
                      <div className="relative" data-id="element-448">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-449">
                          <Tag size={18} className="text-slate-400" data-id="element-450" />
                        </div>
                        <input type="text" value={newInvoice.subCategory} onChange={e => setNewInvoice({
                      ...newInvoice,
                      subCategory: e.target.value
                    })} placeholder="e.g. Dairy" className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all" data-id="element-451" />
                      </div>
                    </div>

                    <div data-id="element-452">
                      <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider" data-id="element-453">
                        Amount
                      </label>
                      <div className="relative" data-id="element-454">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-455">
                          <DollarSign size={18} className="text-slate-400" data-id="element-456" />
                        </div>
                        <input type="number" step="0.01" required value={newInvoice.amount} onChange={e => setNewInvoice({
                      ...newInvoice,
                      amount: e.target.value
                    })} placeholder="0.00" className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all" data-id="element-457" />
                      </div>
                    </div>

                    <div data-id="element-458">
                      <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider" data-id="element-459">
                        Date
                      </label>
                      <div className="relative" data-id="element-460">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-461">
                          <Calendar size={18} className="text-slate-400" data-id="element-462" />
                        </div>
                        <input type="date" required value={newInvoice.date} onChange={e => setNewInvoice({
                      ...newInvoice,
                      date: e.target.value
                    })} className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all" data-id="element-463" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-464">
                    <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider" data-id="element-465">
                      Remark
                    </label>
                    <div className="relative" data-id="element-466">
                      <div className="absolute top-3 left-4 pointer-events-none" data-id="element-467">
                        <FileText size={18} className="text-slate-400" data-id="element-468" />
                      </div>
                      <textarea value={newInvoice.remark} onChange={e => setNewInvoice({
                    ...newInvoice,
                    remark: e.target.value
                  })} rows={3} placeholder="Add any special notes or remarks..." className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none" data-id="element-469" />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4" data-id="element-470">
                    <button type="button" onClick={() => setShowInvoiceModal(false)} className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-sm font-bold transition-all" data-id="element-471">
                      Cancel
                    </button>
                    <button type="submit" className="flex-1 py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-500/20 transition-all" data-id="element-472">
                      Add Invoice
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      <div className="flex items-center justify-between" data-id="element-473">
        <div data-id="element-474">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-475">
            Inventory & Purchasing
          </h2>
          <p className="text-sm text-slate-500" data-id="element-476">
            Manage stock levels and vendor invoices
          </p>
        </div>
        <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm" data-id="element-477">
          <TabButton active={activeTab === 'open'} onClick={() => setActiveTab('open')} label="Open Inventory" data-id="element-478" />
          <TabButton active={activeTab === 'receive'} onClick={() => setActiveTab('receive')} label="Receive Invoice" data-id="element-479" />
          <TabButton active={activeTab === 'close'} onClick={() => setActiveTab('close')} label="Close Inventory" data-id="element-480" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-481">
        <div className="lg:col-span-2 space-y-6" data-id="element-482">
          {activeTab === 'receive' && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-483">
              <div className="p-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/50" data-id="element-484">
                <div className="flex items-center gap-2" data-id="element-485">
                  <Truck className="text-blue-500" size={20} data-id="element-486" />
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-487">
                    Purchase History
                  </h3>
                </div>
                <button onClick={() => setShowInvoiceModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-xs font-bold hover:bg-blue-600 transition-all" data-id="element-488">
                  <Plus size={14} data-id="element-489" />
                  Add Invoice
                </button>
              </div>
              <div className="overflow-x-auto" data-id="element-490">
                <table className="w-full text-left" data-id="element-491">
                  <thead data-id="element-492">
                    <tr className="border-b border-slate-50" data-id="element-493">
                      <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-494">
                        Vendor
                      </th>
                      <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-495">
                        Category
                      </th>
                      <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-496">
                        Sub-Category
                      </th>
                      <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right" data-id="element-497">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-498"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50" data-id="element-499">
                    {invoices.map(inv => <tr key={inv.id} className="hover:bg-slate-50 transition-colors group" data-id="element-500">
                        <td className="px-6 py-4" data-id="element-501">
                          <p className="text-sm font-bold text-slate-900" data-id="element-502">
                            {inv.vendor}
                          </p>
                          <p className="text-[10px] text-slate-400" data-id="element-503">
                            {inv.date}
                          </p>
                        </td>
                        <td className="px-6 py-4" data-id="element-504">
                          <span className="px-2 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold rounded-md uppercase" data-id="element-505">
                            {inv.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs font-medium text-slate-600" data-id="element-506">
                          {inv.subCategory}
                        </td>
                        <td className="px-6 py-4 text-sm font-black text-slate-900 text-right" data-id="element-507">
                          ${inv.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-508">
                          <button className="p-2 text-slate-300 hover:text-rose-500 transition-colors" data-id="element-509">
                            <Trash2 size={14} data-id="element-510" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}

          {(activeTab === 'open' || activeTab === 'close') && <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center" data-id="element-511">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-500 mb-6" data-id="element-512">
                <Package size={32} data-id="element-513" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-514">
                {activeTab === 'open' ? 'Starting Inventory' : 'Ending Inventory'}
              </h3>
              <p className="text-sm text-slate-500 mb-8 max-w-xs" data-id="element-515">
                Enter the total dollar value of Food & Non-Alcoholic beverages
                currently in stock.
              </p>
              <div className="relative w-full max-w-xs" data-id="element-516">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} data-id="element-517" />
                <input type="number" value={activeTab === 'open' ? openInv : closeInv} onChange={e => activeTab === 'open' ? setOpenInv(parseFloat(e.target.value) || 0) : setCloseInv(parseFloat(e.target.value) || 0)} className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-2xl font-black text-slate-900 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all" data-id="element-518" />
              </div>
              <button className="mt-8 px-8 py-3 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition-all flex items-center gap-2" data-id="element-519">
                Save Inventory Value <ChevronRight size={16} data-id="element-520" />
              </button>
            </div>}
        </div>

        <div className="space-y-6" data-id="element-521">
          <div className="bg-emerald-600 rounded-2xl p-6 text-white shadow-xl" data-id="element-522">
            <div className="flex items-center gap-2 mb-8" data-id="element-523">
              <Calculator className="text-emerald-200" size={20} data-id="element-524" />
              <h3 className="text-sm font-bold uppercase tracking-wider" data-id="element-525">
                COGS Calculator
              </h3>
            </div>

            <div className="space-y-6" data-id="element-526">
              <div className="flex items-center justify-between text-emerald-100" data-id="element-527">
                <span className="text-xs font-bold uppercase tracking-widest" data-id="element-528">
                  Open Inventory
                </span>
                <span className="text-sm font-bold" data-id="element-529">
                  ${openInv.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between text-emerald-100" data-id="element-530">
                <span className="text-xs font-bold uppercase tracking-widest" data-id="element-531">
                  Weekly Purchases
                </span>
                <span className="text-sm font-bold" data-id="element-532">
                  + ${totalPurchases.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between text-emerald-100" data-id="element-533">
                <span className="text-xs font-bold uppercase tracking-widest" data-id="element-534">
                  Close Inventory
                </span>
                <span className="text-sm font-bold" data-id="element-535">
                  - ${closeInv.toLocaleString()}
                </span>
              </div>

              <div className="pt-6 border-t border-white/20" data-id="element-536">
                <div className="flex items-center justify-between" data-id="element-537">
                  <span className="text-sm font-black uppercase tracking-widest" data-id="element-538">
                    Total COGS
                  </span>
                  <span className="text-2xl font-black" data-id="element-539">
                    ${cogs.toLocaleString()}
                  </span>
                </div>
                <p className="text-[10px] text-emerald-200 mt-2 font-medium" data-id="element-540">
                  Cost of Goods Sold for the current period.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm" data-id="element-541">
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4" data-id="element-542">
              Quick Stats
            </h3>
            <div className="space-y-4" data-id="element-543">
              <div className="flex items-center justify-between" data-id="element-544">
                <span className="text-xs font-bold text-slate-600" data-id="element-545">
                  Active Vendors
                </span>
                <span className="text-xs font-black text-slate-900" data-id="element-546">12</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-547">
                <span className="text-xs font-bold text-slate-600" data-id="element-548">
                  Pending Invoices
                </span>
                <span className="text-xs font-black text-slate-900" data-id="element-549">3</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-550">
                <span className="text-xs font-bold text-slate-600" data-id="element-551">
                  Avg. Weekly Spend
                </span>
                <span className="text-xs font-black text-slate-900" data-id="element-552">
                  $4,250
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function TabButton({
  active,
  onClick,
  label
}: any) {
  return <button onClick={onClick} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${active ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-553">
      {label}
    </button>;
}